import { useState, useRef, useCallback, useMemo, useEffect } from "react";

import { AgGridReact } from "ag-grid-react";
import Typography from "@mui/material/Typography";
import { Chip, ToggleButton, ToggleButtonGroup, Autocomplete, Box, TextField, CardContent } from "@mui/material";
import AppCard, { CARD_HEADER_SX, SECTION_TITLE_SX } from "@plannel/components/ui/AppCard";
import ColorUtil from "@plannel/pages/utils/color-util";
import { withTranslation } from "react-i18next";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import Snackbar from "@plannel/components/Snackbar";
import { FilterButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import PaginationContainer from "@plannel/components/PaginationContainer";
import { MPLocationTreeFilter } from "@plannel/components/filter/LocationTreeFilter";
import SplitterContainer from "@plannel/components/SplitterContainer";
import MpVersionFilter from "@plannel/components/filter/MpVersionFilter";
import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import { isEmpty } from "lodash";
import { useDispatch } from "react-redux";
import inventoryWipService from "@plannel/services/mp/inventoryWip-tracking-service";
import codeUtil from "@plannel/pages/utils/code-util";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import userService from "@plannel/services/system/user-service";
import { FS } from "@plannel/utils/font-util";

const brandTeal = ColorUtil.roundToggleColor.brandTeal;

// 그리드 안 비율 바 — DW 대시보드의 공용 RatioBarCell(components/dashboard/dashboardGrid) 룩에 맞춘다:
// 트랙 action.hover / 높이 8 / radius 0.5 / 회색 11px 텍스트. 채움은 브랜드 청록 단색(이 컬럼은 무지개색 불필요).
const RatioBar = ({ value }) => {
  const pct = Math.min(Math.max((value ?? 0) * 100, 0), 100);
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%", gap: 0.75 }}>
      <Box sx={{ flex: 1, height: 8, bgcolor: "action.hover", borderRadius: 0.5, overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: brandTeal[700] }} />
      </Box>
      <Typography sx={{ minWidth: 34, textAlign: "right", fontSize: FS.BODY, color: "text.secondary" }}>
        {pct.toFixed(1)}%
      </Typography>
    </Box>
  );
};

const CURRENT_DATE = new ZDate();
const INIT_END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), CURRENT_DATE.getDate() + 120);

const { itemType } = codeUtil;
const WIP_ONLY_COLUMNS = ["invCd", "routeName", "resName"];


const InventoryWipTracking = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  const user = JSON.parse(localStorage.getItem("user"));
  const [userRoles, setUserRoles] = useState(reduxViewState?.userRoles ?? []);

  const [viewType, setViewType] = useState(reduxViewState?.viewType ?? "inventory");
  const viewTypeRef = useRef(reduxViewState?.viewType ?? "inventory");
  const [mpVersionFilter, setMpVersionFilter] = useState(reduxViewState?.mpVersion ?? null);
  const mpVersionFilterRef = useRef(null);
  const [filterValue, setFilterValue] = useState(reduxViewState?.filter ?? { locations: null });
  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const bottomItemSearch = useRef(reduxViewState?.bottomItemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const itemTypes = Object.keys(itemType);
  const [itemTypeSearch, setItemTypeSearch] = useState(reduxViewState?.itemTypeSearch ?? null);

  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ?? null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [wipData, setWipData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [totalOrderQty, setTotalOrderQty] = useState(0);

  const pageSizes = [30, 50, 100];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(1);

  const [gridLoading, setGridLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const prevMpVersionIdRef = useRef(reduxViewState?.mpVersion?.id ?? null);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const wipGridRef = useRef();
  const orderGridRef = useRef();

  const [period, setPeriod] = useState(
    reduxViewState?.period
      ? { start: new ZDate(reduxViewState.period.start), end: new ZDate(reduxViewState.period.end) }
      : { start: new ZDate(CURRENT_DATE), end: new ZDate(INIT_END_DATE) }
  );

  const [totalWipQty, setTotalWipQty] = useState(0);
  const [totalUsedQty, setTotalUsedQty] = useState(0);

  const wipGridMemo = useMemo(() => {
    const settings = DefaultGridSetting({ title, viewName });
    settings.defaultColDef.filter = true;
    return settings;
  }, [title, viewName]);

  const orderGridMemo = useMemo(() => {
    const settings = DefaultGridSetting({ title: `${title}_detail`, viewName: `${viewName}_detail` });
    settings.defaultColDef.filter = true;
    return settings;
  }, [title, viewName]);

  const statusBar = useMemo(() => ({
    statusPanels: [
      { statusPanel: "agTotalRowCountComponent", align: "center" },
      { statusPanel: "agAggregationComponent" },
    ],
  }), []);

  const itemTypeMap = itemType; // codeUtil.itemType
  const invTypeMap = {
    "IN_TRANSIT": { name: "intransitInventory" },
    "RECEIPT_PLAN": { name: "receiptPlanInventory" },
    "ON_HAND": { name: "onhandInv" },
    "WIP": { name: "wip" },
  };
  const invTypeFilterOption = viewType === "wip" ? ["wip"] : ["on_hand", "receipt_plan"];

  /*
   * 공정/자원은 WIP 뷰 쿼리에만 있는 컬럼이다(재고 뷰는 z_mp_operation_plan 을 조인하지 않는다).
   * 고급 필터의 컬럼 목록은 hide 가 아니라 filterType 유무로 정해지므로(AdvancedFilter.extractFilterColumns),
   * 재고 뷰에서는 filterType 을 빼서 아예 노출하지 않는다 - 두면 ZMOP 조건이 만들어져 쿼리가 실패한다.
   */
  const wipOnlyFilterType = viewType === "wip" ? "string" : undefined;

  const wipColumnDefs = useMemo(() => [
    { headerName: "locCd", field: "locCd", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "locName", field: "locName", type: ["nonEditableColumn"], width: 150, filterType: "string" },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], cellClass: "stringType", width: 120, filterType: "string" },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 160, filterType: "string" },
    { headerName: "routeName", field: "routeName", type: ["nonEditableColumn"], width: 120, hide: viewType !== "wip", filterType: wipOnlyFilterType },
    { headerName: "resName", field: "resName", type: ["nonEditableColumn"], width: 120, hide: viewType !== "wip", filterType: wipOnlyFilterType },
    { headerName: "wipCd", field: "invCd", type: ["nonEditableColumn"], cellDataType: "text", cellClass: "stringType", width: 130, hide: viewType !== "wip", filterType: "string" },
    { headerName: "invQty", field: "invQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "consmpQty", field: "consmpQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "lastUsedDate", field: "consmpDate", type: ["nonEditableColumn"], width: 130, filterType: "date" },
    { headerName: "invType", field: "invType", type: ["nonEditableColumn"], width: 160, filterType: "string",
      filterOption: invTypeFilterOption,
      valueFormatter: (params) => {
        const code = params.value;
        const key = invTypeMap?.[code]?.name;
        return key ? t(key) : code;
      }
    },
    { headerName: "itemType", field: "itemType", type: ["nonEditableColumn"], width: 160, filterType: "string",
      filterOption: Object.keys(itemTypeMap).map(key => key.toLowerCase()),
      valueFormatter: (params) => {
        const code = params.value;
        return t(itemTypeMap?.[code]?.name) ?? code;
      }
    },
     // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [viewType]);

  const orderColumnDefs =[
    {
      headerName: "orderCd", 
      field: "consmpOrderCd", 
      type: ["nonEditableColumn"], 
      width: 150, 
      cellClass: "stringType",
      cellRenderer: (params) => {
        if (!params.value) return params.value;
        // 주문 추적이 권한 밖이면 링크가 아니라 평문으로 — 밑줄·포인터까지 함께 뺀다.
        if (!isPermitted("ORDER_TRACKING")) return params.value;
        return (
          <span
            style={{ color: "#1976d2", textDecoration: "underline", cursor: "pointer" }}
            onClick={() => onOrderCdClicked(params.value)}
          >
            {params.value}
          </span>
        );
      }
    },
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 120 },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 120 },
    { headerName: "customerCd", field: "customerCd", type: ["nonEditableColumn"], width: 120 },
    { headerName: "customerName", field: "customerName", type: ["nonEditableColumn"], width: 120 },
    { headerName: "dueDate", field: "dueDate", type: ["nonEditableColumn"], width: 120 },
    { headerName: "consmpDate", field: "consmpDate", type: ["nonEditableColumn"], width: 120 },
    { headerName: "consmpQty", field: "consmpQty", type: ["nonEditableColumn", "numberColumn", "rightAligned"], width: 120 },
    { headerName: "ratio", field: "ratio", type: ["nonEditableColumn"], width: 300, cellRenderer: (params) => <RatioBar value={params.value} /> },
  ];

  const onWipGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: wipColumnDefs, viewName, initState: true, alignOrderToDefs: true })
      .catch(e => { debug(e); })
      .finally(() => {
        if (params.api?.isDestroyed?.() === false) {
          params.api.setColumnsVisible(WIP_ONLY_COLUMNS, viewTypeRef.current === "wip");
        }
      });
  };

  const onOrderGridReady = (params) => {
    GridUtils.setColumnDefs({ ...params, columnDefs: orderColumnDefs, viewName: `${viewName}_detail`, initState: true });
  };

  const onWipSelectionChanged = useCallback((params) => {
    const selectedRows = params.api.getSelectedRows();
    const rowData = selectedRows[0] ?? null;

    setSelectedRow(rowData);

    if (!rowData) {
      setOrderData([]);
      return;
    }
    retrieveInventoryWipTracking(rowData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter]);

  const onWipRowDataUpdated = useCallback((params) => {
    const firstRow = params.api.getDisplayedRowAtIndex(0);
    if (!firstRow) {
      setSelectedRow(null);
      setOrderData([]);
      return;
    }
    params.api.deselectAll();
    firstRow.setSelected(true);
  }, []);

  // STEP1 Effect
  useEffect(() => {
    if (isEmpty(userRoles)) {
      retrieveUserRoles();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  useEffect(() => {
    mpVersionFilterRef.current = mpVersionFilter;
  }, [mpVersionFilter]);

  // 버전 선택 시 — 버전 ID가 실제로 바뀔 때만 페이지 리셋 + 기간 파생
  useEffect(() => {
    if (!isEmpty(mpVersionFilter)) {
      if (mpVersionFilter.id !== prevMpVersionIdRef.current) {
        setCurrentPage(1);
        setPeriod(periodCalculate(mpVersionFilter));
      }
      prevMpVersionIdRef.current = mpVersionFilter.id;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mpVersionFilter]);

  // 기간/그리드 준비 변경 → 페이지 유지하고 상단 그리드 + 요약 카드 재조회
  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter)) {
      setReload(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, gridLoading]);

  // 위치/품목유형 필터 변경 → 상단 그리드만 재조회 (list)
  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter)) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValue, itemTypeSearch]);

  // 페이지/페이지크기 변경 → 상단 그리드만 재조회 (list)
  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter)) {
      setReload(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // 고급 필터 색상 변경 토글 + 재조회 (list)
  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }

    if (gridLoading && !isEmpty(mpVersionFilter)) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  // 상단 그리드(list) 단일 조회 깔때기 — 모든 트리거가 reload 한 곳으로 수렴
  useEffect(() => {
    if (gridLoading && !isEmpty(mpVersionFilter) && reload) {
      retrieveInventoryWip();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, gridLoading]);

  // viewType 변경 → 고급 필터 초기화 + 컬럼 가시성 토글 + 페이지 리셋 + 재조회
  useEffect(() => {
    /* 진입(첫 실행)과 실제 뷰 전환을 구분한다 — 진입 때는 redux 에서 복원한 고급 필터를 지우면 안 된다.
       ref 초기값이 viewType 초기값과 같으므로 대입 전에 비교한다. */
    const viewTypeChanged = viewTypeRef.current !== viewType;
    viewTypeRef.current = viewType;

    const api = wipGridRef.current?.api;
    if (api) {
      api.setColumnsVisible(WIP_ONLY_COLUMNS, viewType === "wip");
    }

    /*
     * 뷰가 바뀌면 고급 필터를 비운다. 뷰마다 조회 쿼리가 달라서 한쪽에만 있는 컬럼(공정/자원)의 조건이
     * 남으면 조회가 실패하고, 그 컬럼은 반대 뷰의 필터 목록에도 없어 사용자가 직접 지울 수 없다.
     * (재조회는 아래 initPageToRetrieve 와 함께 reload 깔때기로 수렴하므로 이중 조회가 되지 않는다.)
     */
    if (viewTypeChanged) {
      setAdvancedFilters(null);
    }
    if (gridLoading && !isEmpty(mpVersionFilter)) {
      initPageToRetrieve();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  const onOrderCdClicked = (orderCd) => {
    const ORDER_TRACKING = "ORDER_TRACKING";
    reduxDispatch(reduxUtil.removeViewState({ viewName: ORDER_TRACKING }));
    const reduxState = reduxUtil.createObjectState(ORDER_TRACKING, { orderCd, mpVersion: mpVersionFilterRef.current });
    reduxDispatch(reduxUtil.updateViewState(reduxState));
    reduxDispatch(reduxUtil.updateHistoryState(ORDER_TRACKING));
  };

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (!isEmpty(mpVersionFilter)) {
      searchFilters.versionId = mpVersionFilter.id;
    }

    if (filterValue?.locations) {
      searchFilters.locationCd = filterValue?.locations?.map(location => location?.locCd);
    }

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (period?.start) {
      searchFilters.startDate = period.start;
    }

    if (period?.end) {
      searchFilters.endDate = period.end;
    }

    if (itemTypeSearch) {
      searchFilters.itemType = itemTypeSearch;
    }

    if (viewType) {
      searchFilters.viewType = viewType;
    }

    params.searchFilters = searchFilters;

    if (!isEmpty(advancedFilters) && advancedFilters?.children?.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    return params;
  }

  const retrieveUserRoles = () => {
    userService.getUserAccessRoles({ username: user.username })
      .then(
        (response) => {
          const userAccess = response.data;
          setUserRoles(userAccess?.roles);
        },
      )
      .catch(e => { debug(e); });
  };

  const retrieveInventoryWip = () => {
    wipGridRef?.current?.api?.setGridOption('loading', true);

    const params = getRequestParams();
    setWipData([]);

    inventoryWipService.getInventoryWip(params)
      .then((response) => {
        const { results, totalPages, total, usedDemandCnt } = response.data;
        setGridPages(totalPages ?? 1);
        setWipData(results ?? []);
        setTotalWipQty(total?.totalQty ?? 0);
        setTotalUsedQty(total?.usedQty ?? 0);
        setTotalOrderQty(usedDemandCnt ?? 0);
      })
      .catch((error) => {
        debug(error);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, mpVersion: mpVersionFilter, filter: filterValue, itemTypeSearch: itemTypeSearch, currentPage, pageSize, viewType, advancedFilters, period });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
      .finally(() => {
        wipGridRef.current?.api?.setGridOption('loading', false);
        setReload(false);
      })
  }

  const retrieveInventoryWipTracking = (rowData) => {
    if (!mpVersionFilter || !mpVersionFilter.id) {
      return;
    }

    // 하단은 선택된 상단 행 기준으로만 조회한다 — 행 없이 부르면 거점·품목·수량이 빈 요청이 나간다.
    if (!rowData) {
      setOrderData([]);
      return;
    }

    const requestParams = {
      versionId: mpVersionFilter?.id,
      locationCd: rowData?.locCd,
      itemCd: rowData?.itemCd,
      invQty: rowData?.invQty,
      invCd: rowData?.invCd,
      invType: rowData?.invType,
      orderItemCd: bottomItemSearch.current,
    };

    orderGridRef?.current?.api?.setGridOption('loading', true);
    inventoryWipService.getInventoryWipTracking(requestParams)
      .then((response) => {
        const { results } = response.data;

        const totalQty = rowData?.consmpQty ?? 0;

        const data = (results ?? []).map(item => ({
          ...item,
          ratio: totalQty > 0
            ? item.consmpQty / totalQty
            : 0,
        }));
        setOrderData(data);
      })
      .catch((error) => {
        debug(error);
      })
      .finally(() => {
        orderGridRef.current?.api?.setGridOption('loading', false);
      })
  }


  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      setReload(true);
    }
  }

  const periodCalculate = (version) => {
    let startDate = new ZDate(CURRENT_DATE);
    let endDate = new ZDate(INIT_END_DATE);

    if (!isEmpty(version)) {
      const selectedVersionPlanConfig = version?.planConfigObj;

      if (selectedVersionPlanConfig) {
        const { PLAN_START_DATE: planStartDate, PLAN_END_DATE: planEndDate } = selectedVersionPlanConfig;

        startDate = new ZDate(planStartDate);
        endDate = new ZDate(planEndDate);
      }
    }

    return { start: startDate, end: endDate };
  }

  return (
    <>
      <FilterContainer>
        <MpVersionFilter
          viewName={viewName}
          mpVersionValue={mpVersionFilter}
          onChangeFilter={setMpVersionFilter}
        />
        <PeriodFilter viewName={viewName} period={period} setPeriod={setPeriod} dateType={"day"} />
        <MPLocationTreeFilter
          viewName={viewName}
          filterValue={filterValue?.locations?.map((l) => l?.locCd)}
          onChangeFilter={setFilterValue}
        />
        <MPItemAutocomplete
          retrieve={initPageToRetrieve}
          searchValue={itemSearch}
          extraCodesRef={extraItemCodesRef}
        />
        <Autocomplete
          options={itemTypes}
          getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
          value={itemTypeSearch}
          onChange={(_, newValue) => setItemTypeSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("itemType")} />
          )}
        />
        <Box sx={{ ml: "auto" }}>
        <ToggleButtonGroup
          sx={{ ml: "auto", "& .MuiToggleButton-root": { px: 1.5, py: 0.25, fontSize: FS.SUB, fontWeight: 600, lineHeight: 1.4 } }}
          value={viewType}
          exclusive
          onChange={(_, v) => { if (v) setViewType(v); }}
          size="small"
          color="primary"
        >
          <ToggleButton value="inventory">{t("inventory")}</ToggleButton>
          <ToggleButton value="wip">{t("wip")}</ToggleButton>
        </ToggleButtonGroup>
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={() => setOpenFilter(!openFilter)} />
          {/* <DownloadButton label="exportAll" onClick={() => wipGridRef.current?.api?.exportDataAsExcel()} /> */}
        </Box>
      </FilterContainer>
      <Box sx={{ mt: 1, pb: 1, display: "flex", gap: 1.5 }}>
        <AppCard key="wipQtySum" sx={{ width: "33%"}}>
          <CardContent>
            <Typography key="method1" sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
              {viewType === "wip" ? t("wipQtySum") : t("inventoryQtySum")}
            </Typography>
            <Typography key="method2" sx={{ fontSize: FS.KPI, fontWeight: 600, color: "text.primary" }}>
              {totalWipQty.toLocaleString()}
            </Typography>
          </CardContent>
        </AppCard>
        <AppCard key="usedQty" sx={{ width: "33%" }}>
          <CardContent>
            <Typography key="method1" sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
              {t("usedQty")}
            </Typography>
            <Typography key="method2" sx={{ fontSize: FS.KPI, fontWeight: 600, color: "text.primary" }}>
              {totalUsedQty.toLocaleString()}
            </Typography>
          </CardContent>
        </AppCard>
        <AppCard key="extraQty" sx={{ width: "33%" }}>
          <CardContent>
            <Typography key="method1" sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
              {viewType === "wip" ? t("extraWipQty") : t("extraInventoryQty")}
            </Typography>
            <Typography key="method2" sx={{ fontSize: FS.KPI, fontWeight: 600, color: "text.primary" }}>
              {(totalWipQty - totalUsedQty).toLocaleString()}
            </Typography>
          </CardContent>
        </AppCard>
        <AppCard key="usedOrderCount" sx={{ width: "33%" }}>
          <CardContent>
            <Typography key="method1" sx={{ ...CARD_HEADER_SX, mb: 0.5 }}>
              {t("usedOrderCount")}
            </Typography>
            <Typography key="method2" sx={{ fontSize: FS.KPI, fontWeight: 600, color: "text.primary" }}>
              {totalOrderQty.toLocaleString()}
            </Typography>
          </CardContent>
        </AppCard>
      </Box>

      <Box className="grid-area" sx={{ display: "flex", flexDirection: "row" }}>
        <Box sx={{ flex: 1 }}>
          <SplitterContainer initialSizes={[1.2, 1]} collapseDirection="up">
          <Box height="100%" sx={{ display: "flex", flexDirection: "row" }}>
            <Box height="100%" sx={{ flex: 1 }}>
              <Box className="ag-theme-balham" height="calc(100% - 50px)" sx={{ mb: 1 }}>
                <AgGridReact
                  ref={wipGridRef}
                  rowData={wipData}
                  {...wipGridMemo}
                  statusBar={statusBar}
                  onGridReady={onWipGridReady}
                  onRowDataUpdated={onWipRowDataUpdated}
                  onSelectionChanged={onWipSelectionChanged}
                />
              </Box>
              <PaginationContainer
                currentPage={currentPage} totalPages={gridPages}
                pageSize={pageSize} pageSizes={pageSizes}
                setCurrentPage={setCurrentPage} setPageSize={setPageSize}
              />
            </Box>
          </Box>
          <Box height="100%" sx={{ display: "flex", flexDirection: "column" }}>
            {/* 패널 제목 → 검색창 → 선택 요약(칩+건수) 순으로 한 줄에 배치한다. */}
            <FilterContainer hasIcons={false} sx={{ pt: 2, alignItems: "center" }}>
              <Typography variant="h6" sx={{ ...SECTION_TITLE_SX, mr: 2 }}>{viewType === "wip" ? t("wipUsagebyOrder") : t("inventoryUsagebyOrder")}</Typography>
              <MPItemAutocomplete
                retrieve={() => retrieveInventoryWipTracking(selectedRow)}
                searchValue={bottomItemSearch}
              />
              {selectedRow && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 1 }}>
                  <Chip
                    label={`${selectedRow.locCd ?? ""} · ${selectedRow.itemCd ?? ""}`}
                    size="small"
                    sx={{ ...ColorUtil.tintChipSx(brandTeal[700]), fontSize: FS.SUB }}
                  />
                  <Typography sx={{ fontSize: FS.TITLE, color: "text.secondary" }}>
                    {viewType === "wip" ? t("orderSelectedWip") : t("orderSelectedInventory")} {orderData.length} {t("case")}
                  </Typography>
                </Box>
              )}
            </FilterContainer>
            <Box className="ag-theme-balham" sx={{ flex: 1 }}>
              <AgGridReact
                ref={orderGridRef}
                rowData={orderData}
                {...orderGridMemo}
                statusBar={statusBar}
                onGridReady={onOrderGridReady}
              />
            </Box>
          </Box>
        </SplitterContainer>
        </Box>
        {openFilter && (
          <ResizableFilterPanel defaultWidth={450}>
            {/* key 로 뷰가 바뀔 때 리마운트한다 — 확정(onApply)까지 가지 않은 편집 중 조건까지 비우려면
                advancedFilters 를 null 로 되돌리는 것만으로는 부족하다(이미 null 이면 prop 이 안 바뀌어
                컴포넌트 내부 편집 상태가 그대로 남는다). */}
            <AdvancedFilter
              key={viewType}
              columnDefs={wipColumnDefs}
              advancedFilters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setOpenFilter(false)}
            />
          </ResizableFilterPanel>
        )}
      </Box>

      <Snackbar
        open={snackInfo.open}
        severity={snackInfo.severity}
        content={snackInfo.content}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      />
    </>
  );
};

export default withTranslation()(InventoryWipTracking);